import { useFormik } from 'formik';
import { BiLogoInstagram } from 'react-icons/bi';
import { BsTwitterX } from 'react-icons/bs';
import { FaFacebookF } from 'react-icons/fa';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';

import Button from './Button';
import InputField from './InputField';
import { Loader } from './Loader';
import Modal from './Modal';

import LogoWhite from '@/assets/ic_gdt-white.svg';
import Form from '@/components/Form';
import useAuth from '@/hooks/useAuth';
import useConfirmLogout from '@/hooks/useConfirmLogout';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import { inviteFriendGdt } from '@/services/invites';
import { ModalActions } from '@/types';
import { FormData } from '@/types/invites';
import { cn, inviteFriendSchema, validateFieldError } from '@/utils';

type Props = {
  className?: string;
  isPremium?: boolean;
};

export const FooterComplex = ({ className, isPremium }: Props) => {
  const toast = useToast();
  const { isAuthenticated } = useAuth();
  const { renderExitModal, showConfirmLogoutModal } = useConfirmLogout();
  const { isMobile } = useIsDesktop();

  const { mutate: postData, isLoading } = useMutation(
    async (data: FormData) => await inviteFriendGdt(data),
    {
      onSuccess: (data) => {
        if (data.estado && data.enviado) {
          toast.show(data.mensaje);
        } else {
          toast.show(data.mensaje);
        }
      },
      onError: (err) => {
        const error = err as Error;
        toast.show(error.message);
      },
    }
  );

  const {
    touched,
    isValid,
    errors,
    isSubmitting,
    isValidating,
    handleChange,
    handleSubmit,
    values,
    handleBlur,
    resetForm,
  } = useFormik({
    initialValues: {
      name: '',
      email: '',
    },
    validateOnMount: false,
    validateOnBlur: true,
    validationSchema: inviteFriendSchema,
    onSubmit: (val: FormData, { setSubmitting }) => {
      postData(val);
      setSubmitting(false);
    },
  });

  if (isMobile) return <></>;

  const formRender = (props: ModalActions) => {
    const { closeModal } = props;

    return (
      <div>
        <p className="text-center mt-2 text-sm font-medium mb-7">
          Ingresá sus datos y enviale tu invitación{' '}
        </p>
        <Form.Form loading={isSubmitting} onSubmit={handleSubmit}>
          <Form.Field className="space-y-5">
            <InputField
              autoComplete="off"
              behavior="default"
              error={validateFieldError(
                touched.name,
                errors.name,
                values.name.length
              )}
              label="Nombre"
              name="name"
              placeholder="Ingresar"
              success={!errors.name && values.name.length > 0}
              type="text"
              value={values.name}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <InputField
              autoComplete="off"
              behavior="default"
              error={validateFieldError(
                touched.email,
                errors.email,
                values.email.length
              )}
              label="E-mail"
              name="email"
              placeholder="Ingresar"
              success={!errors.email && values.email.length > 0}
              type="text"
              value={values.email}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </Form.Field>
          <div className="flex items-center justify-evenly mt-7 space-x-2">
            <Button
              className="font-bold flex-1"
              id="btnCancelar"
              type="button"
              variant="secondary"
              onClick={() => closeModal()}
            >
              Cancelar
            </Button>
            <Button
              className="font-bold flex-1"
              disabled={!isValid || isValidating || !values.email || isLoading}
              id="btnFormSubmit"
              type="submit"
              variant="primary"
            >
              Enviar
            </Button>
          </div>
        </Form.Form>
      </div>
    );
  };

  return (
    <>
      {<Loader isVisible={isLoading} timeout={true} />}
      <footer
        className={cn(
          `footerComplex w-full relative pt-6 text-white underline-offset-4 text-xs font-medium`,
          {
            'bg-gradient-to-b from-[#1F4CD3] to-[#1E2545]': !isPremium,
            'premium !bg-gdt-gray from-gdt-gray to-gdt-gray': isPremium,
          },
          className
        )}
      >
        <div className="flex flex-col items-center">
          <div className="flex items-center w-full max-w-[71rem] px-4">
            <figure className="flex-[1_1_13rem]">
              <img
                alt="Logo Gran Dt"
                className="max-w-[2.5rem]"
                src={LogoWhite}
              />
            </figure>
            <div className="w-full flex justify-center">
              <Link
                className="hover:text-gray-300 underline"
                id="linkRegulation"
                target="_blank"
                to="/pdf/Reglamento_Gran_DT.pdf"
              >
                Reglamento
              </Link>
              <span className="mx-[.9rem] font-light">|</span>
              <Link
                className="hover:text-gray-300 underline"
                id="linkFaq"
                rel="noopener noreferrer"
                target="_blank"
                to={ROUTES.preguntasFrecuentes}
              >
                Preguntas Frecuentes
              </Link>
              <span className="mx-[.9rem] font-light">|</span>
              <Link
                className="hover:text-gray-300 underline"
                id="linkPolitics"
                target="_blank"
                to="/pdf/Reglamento_Gran_DT.pdf#page=23"
              >
                Política de Privacidad
              </Link>
              <span className="mx-[.9rem] font-light">|</span>
              <Link
                className="hover:text-gray-300 underline"
                id="linkContact"
                rel="noopener noreferrer"
                target="_blank"
                to={ROUTES.preguntasFrecuentes}
              >
                Contacto
              </Link>
              <span className="mx-[.9rem] font-light">|</span>
              <Modal
                render={(props: ModalActions) => {
                  return formRender(props);
                }}
                size="xs"
                title="Invitá a un amigo"
                trigger={({ openModal }) => {
                  return (
                    <Button
                      className="hover:text-gray-300 text-white underline text-xs"
                      id="linkInvite"
                      type="button"
                      variant="text"
                      onClick={() => {
                        openModal();
                        resetForm();
                      }}
                    >
                      Invitar a un amigo
                    </Button>
                  );
                }}
                variant="default"
              />
              {isAuthenticated && (
                <>
                  <span className="mx-[.9rem]  font-light">|</span>
                  <Link
                    className="hover:text-gray-300 underline"
                    id="linkCloseSession"
                    to="#"
                  >
                    <span onClick={() => showConfirmLogoutModal()}>
                      Cerrar sesión
                    </span>
                  </Link>
                </>
              )}
            </div>
            <div className="flex-[1_1_13rem] flex items-center justify-end space-x-8">
              <Link
                target="_BLANK"
                to="https://www.facebook.com/GranDTOficial/"
              >
                <FaFacebookF className="text-lg" />
              </Link>
              <Link target="_BLANK" to="https://twitter.com/GranDToficial/">
                <BsTwitterX className="text-lg" />
              </Link>
              <Link
                target="_BLANK"
                to="https://www.instagram.com/grandtoficial/"
              >
                <BiLogoInstagram className="text-[1.5rem]" />
              </Link>
            </div>
          </div>

          <hr
            className={cn(`w-full mt-[18px] mb-[25px]`, {
              'border-[#244AA8]': !isPremium,
              'border-gdt-gray-3': isPremium,
            })}
          />

          <div className="flex justify-center flex-wrap pb-5">
            <div className="flex items-center font-light">
              <Link
                className="text-2xs hover:text-gray-300 underline mt-[-.3rem] tracking-wider"
                id="linkEmail"
                target="_blank"
                to="mailto:consultas@grandt.com.ar"
              >
                consultas@grandt.com.ar
              </Link>
              <span className="mx-[.9rem] font-light">|</span>
              <Link
                className="hover:text-gray-300 font-medium"
                id="linkPhoneFree"
                to="tel:+5491108003332343"
              >
                0800-333-2343
              </Link>
            </div>
            <p className="w-full text-center mt-2.5 font-light">
              Copyright 2008 /{' '}
              <Link
                className="ml-2 font-medium underline"
                target="_blank"
                to="https://www.clarin.com/"
              >
                Clarín
              </Link>
            </p>
          </div>
        </div>
        {renderExitModal()}
      </footer>
    </>
  );
};
